/**
 * Shared client-side store for starred SEC filings.
 *
 * Round 92o++++++++N++++++++++ (May 2026). One source of truth so
 * every Star button across the app (TenK8KRisksTab, Watchlist strip,
 * future Bell popover) reads consistent state without each one
 * round-tripping to /api/filings/starred separately.
 *
 * API:
 *   • subscribe(listener)  → returns unsubscribe; fires listener on
 *                            every state mutation.
 *   • getSnapshot()        → current in-memory map keyed by
 *                            accession_number.
 *   • hydrate()            → fetch the full list from the backend.
 *                            Idempotent — safe to call from every
 *                            consumer mount.
 *   • star(payload)        → POST, optimistic local update.
 *   • unstar(accession)    → DELETE, optimistic local update.
 *   • isStarred(accession) → boolean.
 *
 * The store is intentionally global module-state (not React Context)
 * so non-React surfaces (modals mounted in portals) can read it too.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const state = {
  byAccession: new Map(), // accession → starred-row payload
  byTicker: new Map(),    // ticker → Set of accession strings
  hydrated: false,
  hydrating: null,        // in-flight promise (dedupe concurrent hydrate calls)
};
// Stable snapshot reference — only mutated via `emit()`. React's
// useSyncExternalStore demands a stable === reference between calls
// when nothing has changed, otherwise it triggers an infinite render
// loop. We bump the reference (object identity) on every mutation
// so React detects a change exactly once per logical update.
let snapshot = { version: 0, byAccession: state.byAccession, byTicker: state.byTicker, hydrated: false };
const listeners = new Set();

function emit() {
  snapshot = {
    version: snapshot.version + 1,
    byAccession: state.byAccession,
    byTicker: state.byTicker,
    hydrated: state.hydrated,
  };
  for (const l of listeners) {
    try { l(); } catch { /* ignore listener errors */ }
  }
}

function indexRow(row) {
  if (!row || !row.accession) return;
  state.byAccession.set(row.accession, row);
  const t = row.ticker || '';
  if (!state.byTicker.has(t)) state.byTicker.set(t, new Set());
  state.byTicker.get(t).add(row.accession);
}

function dropRow(accession) {
  const row = state.byAccession.get(accession);
  if (!row) return;
  state.byAccession.delete(accession);
  const set = state.byTicker.get(row.ticker || '');
  if (set) {
    set.delete(accession);
    if (set.size === 0) state.byTicker.delete(row.ticker || '');
  }
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot() {
  return snapshot;
}

export function isStarred(accession) {
  return !!(accession && state.byAccession.has(accession));
}

export function listForTicker(ticker) {
  const set = state.byTicker.get((ticker || '').toUpperCase());
  if (!set) return [];
  return [...set].map((acc) => state.byAccession.get(acc)).filter(Boolean);
}

export async function hydrate({ force = false } = {}) {
  if (state.hydrated && !force) return;
  if (state.hydrating) return state.hydrating;
  state.hydrating = (async () => {
    try {
      const r = await axios.get(`${API}/filings/starred`, { withCredentials: true });
      const rows = Array.isArray(r.data?.rows) ? r.data.rows : [];
      state.byAccession.clear();
      state.byTicker.clear();
      for (const row of rows) indexRow(row);
      state.hydrated = true;
      emit();
    } catch {
      // Non-admin (or paywalled) → 402. Silent — the button still
      // renders but in non-starred state.
      state.hydrated = true;
      emit();
    } finally {
      state.hydrating = null;
    }
  })();
  return state.hydrating;
}

/**
 * Star a filing. `payload` must include ticker + accession + form_type
 * at minimum. Optimistic — flips local state before the network call
 * lands so the UI feels instant; rolls back on failure.
 */
export async function star(payload) {
  if (!payload || !payload.accession || !payload.ticker) {
    throw new Error('star() requires accession and ticker');
  }
  const acc = payload.accession;
  const previous = state.byAccession.get(acc);
  // Optimistic insert.
  indexRow({ ...payload, starred_at: new Date().toISOString() });
  emit();
  try {
    const resp = await axios.post(`${API}/filings/star`, payload, { withCredentials: true });
    // Round 92o++++++++N++++++++++ (May 2026) — broadcast watchlist
    // auto-add. The backend side-effects an insert into
    // `db.portfolios` when the starred ticker isn't yet on the
    // watchlist. Dispatch a custom event so Dashboard's local
    // `watchlist` state appends the ticker → the header chip
    // counter ("Watchlist 4") updates immediately. Without this,
    // the chip stays stale at the pre-star count until next page
    // navigation.
    if (resp?.data?.watchlist_auto_added && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('watchlist-auto-added', {
        detail: { ticker: payload.ticker },
      }));
    }
  } catch (e) {
    // Roll back on failure.
    if (previous) indexRow(previous); else dropRow(acc);
    emit();
    throw e;
  }
}

export async function unstar(accession) {
  if (!accession) return;
  const previous = state.byAccession.get(accession);
  dropRow(accession);
  emit();
  try {
    await axios.delete(`${API}/filings/star`, {
      params: { accession },
      withCredentials: true,
    });
  } catch (e) {
    if (previous) indexRow(previous);
    emit();
    throw e;
  }
}

/**
 * Round 92o++++++++N++++++++++ (May 2026) — cascade helpers.
 *
 * The backend cascade-deletes `user_starred_filings` rows whenever
 * the user un-watches a ticker (`DELETE /portfolio/{ticker}`) or
 * clears the whole watchlist (`POST /watchlist/clear`). These
 * helpers keep the local store in sync so the ★ icons in the
 * Stock Modal's 10K/8K/Risks tab flip back to outline IMMEDIATELY
 * — no stale gold stars left over from a vanished watchlist row.
 */
export function forgetTicker(ticker) {
  if (!ticker) return;
  const t = ticker.toUpperCase();
  const set = state.byTicker.get(t);
  if (!set || set.size === 0) return;
  for (const acc of [...set]) {
    state.byAccession.delete(acc);
  }
  state.byTicker.delete(t);
  emit();
}

export function forgetAll() {
  if (state.byAccession.size === 0 && state.byTicker.size === 0) return;
  state.byAccession.clear();
  state.byTicker.clear();
  emit();
}
